import { plainToClass } from "class-transformer";
import { Appendix } from "../../../../../../shared/entities/scripts/appendix";
import { Study } from "../../../../../../shared/entities/scripts/study";
import { FilterOption } from "../../../../../../shared/scripts/filtering/filtering.entities";
import { SetActiveTab } from "../../../../../../shared/scripts/tab-utilities";
import { SiteInfo } from "../../../../sites/shared/sites.entities";
import { ISiteInfo } from "../../../scripts/data-completeness.page";
import { VisitsDatatableView } from "../visits-datatable-view/visits-datatable-view";
import { VisitsDatatableEntry } from "../visits-datatable-view/visits-datatable.entities";
import { VisitsSummaryView } from "../visits-summary-view/visits-summary-view";
import { VisitSummarySeries } from "../visits-summary-view/visits-summary.entities";
import { Visit, VisitEvent } from "./visits.entities";
import { VisitLookup } from "./visits.lookup";

export class VisitsController {

    siteInfo: ISiteInfo;

    allVisits: Visit[] = [];
    allVisitEvents: VisitEvent[] = [];
    allVisitRequired: FilterOption[] = [];

    summaryView: VisitsSummaryView;
    datatableView: VisitsDatatableView;

    //selectedVisitEvents: VisitEvent[];

    constructor(siteInfo: ISiteInfo, visitTimingJson: any) {

        this.siteInfo = siteInfo;

        let foundVisitIds: number[] = [];
        let foundVisitRequiredIds: number[] = [];
        this.allVisitEvents = plainToClass(VisitEvent, <VisitEvent[]>visitTimingJson);
        this.allVisitEvents.forEach((v: VisitEvent) => {
            v.initialize()

            if (!foundVisitIds.includes(v.VisitId)) {
                this.allVisits.push(new Visit(v.VisitId, VisitLookup.GetVisit(v.VisitId), VisitLookup.GetVisitOrdering(v.VisitId)));
                foundVisitIds.push(v.VisitId);
            }

            if (!foundVisitRequiredIds.includes(v.RequiredId)) {
                this.allVisitRequired.push(new FilterOption(v.RequiredId, VisitLookup.GetRequired(v.RequiredId)));
                foundVisitRequiredIds.push(v.RequiredId);
            }

        });
        this.allVisits.sort((a: Visit, b: Visit) => a.OrderBy - b.OrderBy);
        this.allVisitRequired.sort((a: FilterOption, b: FilterOption) => a.Id - b.Id);

        this.summaryView = new VisitsSummaryView();
        this.datatableView = new VisitsDatatableView();

        //console.log(this.allVisits);
        //console.log(this.allVisitEvents);
    }

    FilterUpdated(studyIds: number[], appendixIds: number[], siteIds: number[], visitIds: number[], visitRequiredIds: number[]): void {

        let byVisitSummaryMap: Map<number, VisitSummarySeries> = new Map<number, VisitSummarySeries>();
        let bySiteSummaryMap: Map<number, VisitSummarySeries> = new Map<number, VisitSummarySeries>();

        let noGroupingDataMap: Map<string, VisitsDatatableEntry> = new Map<string, VisitsDatatableEntry>();
        let groupingByVisitDataMap: Map<string, VisitsDatatableEntry> = new Map<string, VisitsDatatableEntry>();
        let groupingBySiteDataMap: Map<string, VisitsDatatableEntry> = new Map<string, VisitsDatatableEntry>();

        //for loop instead of forEach for performance given there could be tens of thousands of visit events across all trials
        for (let i = 0; i < this.allVisitEvents.length; ++i) {
            let visit: VisitEvent = this.allVisitEvents[i];

            if (!studyIds.includes(visit.StudyId) || !appendixIds.includes(visit.AppendixId) ||
                !visitRequiredIds.includes(visit.RequiredId) || !visitIds.includes(visit.VisitId) ||
                !siteIds.includes(visit.SiteNumber)) continue;

            //Summary View Data
            let visitSeries: VisitSummarySeries;
            if (byVisitSummaryMap.has(visit.VisitId)) { visitSeries = byVisitSummaryMap.get(visit.VisitId); }
            else { visitSeries = new VisitSummarySeries(VisitLookup.GetVisit(visit.VisitId), "visit", visit.VisitId); }
            visitSeries.addVisitEvent(visit);
            byVisitSummaryMap.set(visit.VisitId, visitSeries);

            let siteSeries: VisitSummarySeries;
            if (bySiteSummaryMap.has(visit.SiteNumber)) { siteSeries = bySiteSummaryMap.get(visit.SiteNumber); }
            else { siteSeries = new VisitSummarySeries(this.siteInfo.GetSiteName(visit.SiteNumber), "site", visit.SiteNumber); }
            siteSeries.addVisitEvent(visit);
            bySiteSummaryMap.set(visit.SiteNumber, siteSeries);

            //Datatable View Data
            let dataHash: string;

            let noGroupingData: VisitsDatatableEntry;
            dataHash = VisitsDatatableEntry.getHash("none", visit);
            if (noGroupingDataMap.has(dataHash)) { noGroupingData = noGroupingDataMap.get(dataHash); }
            else { noGroupingData = new VisitsDatatableEntry(Study.GetStudyName(visit.StudyId), Appendix.GetAppendixName(visit.AppendixId), this.siteInfo.GetSiteName(visit.SiteNumber), SiteInfo.FormatSiteNumber(visit.SiteNumber), VisitLookup.GetVisit(visit.VisitId)); }
            noGroupingData.addVisitEvent(visit);
            noGroupingDataMap.set(dataHash, noGroupingData);

            let visitGroupingData: VisitsDatatableEntry;
            dataHash = VisitsDatatableEntry.getHash("by-visit", visit);
            if (groupingByVisitDataMap.has(dataHash)) { visitGroupingData = groupingByVisitDataMap.get(dataHash); }
            else { visitGroupingData = new VisitsDatatableEntry(Study.GetStudyName(visit.StudyId), Appendix.GetAppendixName(visit.AppendixId), "All Sites", "All Sites", VisitLookup.GetVisit(visit.VisitId)); }
            visitGroupingData.addVisitEvent(visit);
            groupingByVisitDataMap.set(dataHash, visitGroupingData);

            let siteGroupingData: VisitsDatatableEntry;
            dataHash = VisitsDatatableEntry.getHash("by-site", visit);
            if (groupingBySiteDataMap.has(dataHash)) { siteGroupingData = groupingBySiteDataMap.get(dataHash); }
            else { siteGroupingData = new VisitsDatatableEntry(Study.GetStudyName(visit.StudyId), Appendix.GetAppendixName(visit.AppendixId), this.siteInfo.GetSiteName(visit.SiteNumber), SiteInfo.FormatSiteNumber(visit.SiteNumber), "All Visits"); }
            siteGroupingData.addVisitEvent(visit);
            groupingBySiteDataMap.set(dataHash, siteGroupingData);
        }

        let seriesByVisit: VisitSummarySeries[] = [...byVisitSummaryMap.values()];
        seriesByVisit.sort((a: VisitSummarySeries, b: VisitSummarySeries) => a.sortValue - b.sortValue);

        let seriesBySite: VisitSummarySeries[] = [...bySiteSummaryMap.values()];
        seriesBySite.sort((a: VisitSummarySeries, b: VisitSummarySeries) => b.sortValue - a.sortValue);

        this.summaryView.UpdateData(seriesByVisit, seriesBySite);


        let tableEntriesNone: VisitsDatatableEntry[] = [...noGroupingDataMap.values()];
        for (let i: number = 0; i < tableEntriesNone.length; ++i) { tableEntriesNone[i].initialize(); }
        //sort it somehow

        let tableEntriesByVisit: VisitsDatatableEntry[] = [...groupingByVisitDataMap.values()];
        for (let i: number = 0; i < tableEntriesByVisit.length; ++i) { tableEntriesByVisit[i].initialize(); }

        let tableEntriesBySite: VisitsDatatableEntry[] = [...groupingBySiteDataMap.values()];
        for (let i: number = 0; i < tableEntriesBySite.length; ++i) { tableEntriesBySite[i].initialize(); }

        //console.log(noGroupingDataMap);

        this.datatableView.UpdateData(tableEntriesNone, tableEntriesByVisit, tableEntriesBySite);
    }

    setView(newView: string): void {

        //console.log("visits.setView", newView);
        if (newView == "view-summary") { SetActiveTab("visits-tabs", "visits-summary-content"); this.summaryView.SetView("visit-summary"); }
        else if (newView == "view-datatable") { SetActiveTab("visits-tabs", "visits-datatable-content"); this.datatableView.SetView("visit-datatable"); }
        else {
            this.summaryView.SetView(newView);
            this.datatableView.SetView(newView);
        }
    }

    exportTable(): void {
        this.datatableView.exportTable();
    }
}